import React, { useMemo } from 'react'
import { Menu } from 'antd'
import { usePathname, useRouter } from 'next/navigation'
import { useContext } from 'use-context-selector'
import { useTranslation } from 'react-i18next'
import {
  filterNavTree,
  MAAS_SIDEBAR_NODES,
  MLOPS_SIDEBAR_NODES,
  navNodesToReactRouteItems,
} from '@trainkr/navigation'
import type { RouteItem } from '@/fe-common/configs/routes'
import { routes } from '@/fe-common/configs/routes'
import IconFont from '@/app/components/icon-font'
import AppContext from '@/context/app-context'
import type { ConfigContextValue } from '@/context/config-context'
import { useConfigContext } from '@/context/config-context'
import { usePermissionContext } from '@/context/permission-context'

type MenuItem = Required<Parameters<typeof Menu>[0]>['items'][number]

/** 与配置里 path 对齐（Next 为 /ecs/...、/maas/...，配置为 /llm/...） */
function toConfigPath(pathname: string): string {
  if (pathname.startsWith('/llm')) return pathname
  return `/llm${pathname}`
}

function routeTreeContainsPath(items: RouteItem[], configPath: string): boolean {
  for (const it of items) {
    if (it.path && configPath.startsWith(it.path)) return true
    if (it.children?.length && routeTreeContainsPath(it.children, configPath))
      return true
  }
  return false
}

/** 当前 URL 属于人工智能下哪一个产品模块（机器学习 / 模型服务 / AI 应用构建） */
function findAiModuleForPath(pathname: string): RouteItem {
  const configPath = toConfigPath(pathname)
  for (const mod of routes.ai) {
    if (mod.children?.length && routeTreeContainsPath(mod.children, configPath))
      return mod
  }
  return routes.ai[0]
}

function filterRoutesNode(route: RouteItem, userProfile: { is_super?: boolean }): RouteItem | null {
  if (!route.children) {
    return route
  }
  let children = route.children.filter(child => {
    if (child.adminOnly) return !!userProfile.is_super
    return true
  })
  children = children.reduce((acc: RouteItem[], r: RouteItem) => {
    const filtered = filterRoutesNode(r, userProfile)
    if (filtered) acc.push(filtered)
    return acc
  }, [])
  if (children.length === 0) return null
  return { ...route, children }
}

function filterRoutesList(items: RouteItem[], userProfile: { is_super?: boolean }): RouteItem[] {
  return items
    .map(r => filterRoutesNode(r, userProfile))
    .filter((r): r is RouteItem => r != null)
}

const routes2MenuItem = (
  routeList: any[],
  _config: ConfigContextValue,
  t: (_key: string) => string,
): MenuItem[] => {
  return routeList
    .map(route => {
      const path = route.path?.replace('/llm', '') || ''
      return {
        type: route.children ? 'group' : undefined,
        key: path || route.title,
        label: route.msg ? t(route.msg) : t(`routerConfig.${route.title}`),
        icon: route.children ? undefined : getMenuIcon(route),
        children: route.children
          ? routes2MenuItem(route.children, _config, t)
          : undefined,
      } as MenuItem
    })
    .filter(item => {
      if (item && 'type' in item && item.type === 'group') {
        return (
          item.children &&
          Array.isArray(item.children) &&
          item.children.length > 0
        )
      }
      return true
    })
}

export function getIcon(menu: RouteItem) {
  if (menu.icon) {
    return `icon-tkr${menu.icon}`
  }
  if (menu.path?.startsWith('/iaas') || menu.path?.startsWith('/llm')) {
    const res = menu.path.split('/').splice(2)
    return `icon-tkrres-${res.join('-')}`
  }
  return 'icon-tkrmenu-appstore'
}

function getMenuIcon(menu: RouteItem) {
  return (
    <div className='sidebar-menu-icon'>
      <IconFont type={getIcon(menu)} width={18} />
    </div>
  )
}

export function getSelectedKey(
  pathname: string,
  items: RouteItem[],
): string | undefined {
  for (const item of items) {
    if (item.path && pathname.startsWith(item.path)) {
      return item.path
    } else if (item.children) {
      const selectedKey = getSelectedKey(pathname, item.children)
      if (selectedKey) {
        return selectedKey
      }
    }
  }
  return undefined
}

const App: React.FC = () => {
  const { userProfile } = useContext(AppContext)
  const { isAdminMode } = usePermissionContext()
  const pathname = usePathname()
  const router = useRouter()
  const { t } = useTranslation()
  const config = useConfigContext()

  const navCtx = useMemo(
    () => ({
      isSuperAdmin: !!userProfile?.is_super,
      isAdminMode,
      flags: {
        maasEnabled: config.maas?.enabled,
        billingEnabled: config.billing?.enabled,
        trainEnabled: config.mlops?.train?.enabled,
        deployEnabled: config.mlops?.deploy?.enabled,
      },
    }),
    [
      userProfile?.is_super,
      isAdminMode,
      config.maas?.enabled,
      config.billing?.enabled,
      config.mlops?.train?.enabled,
      config.mlops?.deploy?.enabled,
    ],
  )

  const activeModule = useMemo(
    () => findAiModuleForPath(pathname),
    [pathname],
  )

  const items: RouteItem[] = useMemo(() => {
    if (activeModule.title === 'ml') {
      return navNodesToReactRouteItems(
        filterNavTree(MLOPS_SIDEBAR_NODES, navCtx, 'react'),
      )
    }
    if (activeModule.title === 'maasServiceNav') {
      return navNodesToReactRouteItems(
        filterNavTree(MAAS_SIDEBAR_NODES, navCtx, 'react'),
      )
    }
    return filterRoutesList(activeModule.children ?? [], userProfile ?? {})
  }, [activeModule, navCtx, userProfile])

  const defaultKey =
    activeModule.title === 'maasServiceNav'
      ? '/llm/maas/models'
      : '/llm/ecs/overview'

  const selectedKey =
    getSelectedKey(toConfigPath(pathname), items)?.replace('/llm', '') ||
    defaultKey.replace('/llm', '')

  return (
    <Menu
      style={{ width: 180, overflow: 'auto', height: '100%' }}
      selectedKeys={[selectedKey]}
      selectable={true}
      mode='inline'
      items={routes2MenuItem(items, config, t)}
      onClick={({ key }) => {
        if (key && typeof key === 'string' && key.startsWith('/')) {
          router.push(`${process.env.NEXT_PUBLIC_BASE_URL || ''}${key}`)
        }
      }}
    />
  )
}

export default App
